import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription, switchMap } from 'rxjs';
import { GradingApiService } from './grading-api.service';
import { GradingStateService } from './grading-state.service';
import { FilesService } from './files.service';
import {
  ChatMessage,
  ChatMessageRole,
  ChatMessageStatus,
  ToolCallStatus,
  StandardToolOutput,
} from '../grading.types';
import { AmplitudeService } from '../../../../common/analytics/amplitude.service';

/**
 * Service for handling chat message operations.
 * Manages sending messages and processing streamed responses.
 */
@Injectable()
export class GradingMessageService {
  private readonly gradingApiService = inject(GradingApiService);
  private readonly stateService = inject(GradingStateService);
  private readonly filesService = inject(FilesService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly amplitudeService = inject(AmplitudeService);

  // Track current SSE subscription for cleanup
  private currentStreamSubscription: Subscription | null = null;

  // Track tool calls we've already fired events for (prevents duplicates)
  private readonly trackedToolCalls = new Set<string>();

  /**
   * Send a message and receive AI response via SSE streaming.
   * Includes any pending attachments that are fully uploaded.
   * Messages are streamed as they are generated, with loading states.
   */
  sendMessage(content: string): void {
    const session = this.stateService.session;
    if (!session) {
      console.error('Cannot send message: no active session');
      return;
    }
    const sessionId = session.id;

    // Get completed attachments and their resource IDs
    const completedAttachments = this.filesService.clearCompletedAttachments();
    const resourceIds = completedAttachments
      .map((a) => a.resourceId)
      .filter((id): id is string => !!id);

    // Optimistically add user message to UI
    const userMessage: ChatMessage = {
      id: `temp-${Date.now()}`,
      role: ChatMessageRole.USER,
      content,
      resourceIds,
      resources: completedAttachments.map((a) => ({
        id: a.resourceId!,
        filename: a.fileName,
        originalName: a.fileName,
        mimeType: a.mimeType,
        size: a.size,
        url: '',
        signedUrl: a.signedUrl,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      })),
      status: ChatMessageStatus.DONE,
      createdAt: new Date(),
    };

    this.stateService.addMessage(userMessage);
    this.stateService.setSending(true);
    this.stateService.setError(null);

    // Track message sent (count user messages including this one)
    const userMessageCount = session.messages.filter(
      (m) => m.role === ChatMessageRole.USER
    ).length + 1;
    this.amplitudeService.trackEvent('grading_message_sent', {
      hasText: content.trim().length > 0,
      attachmentCount: resourceIds.length,
      messageLength: content.length,
      messageNumber: userMessageCount,
    });

    // Cancel any existing stream subscription
    this.cancelCurrentStream();

    // Send message and connect to SSE stream
    this.currentStreamSubscription = this.gradingApiService
      .sendMessage(sessionId, content, resourceIds)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        // Once we have the groupId, connect to SSE stream
        switchMap((response) => {
          // Update user message with proper groupId
          this.updateUserMessageWithGroupId(userMessage.id, response.groupId);

          // Connect to SSE stream
          return this.gradingApiService.streamMessages(
            sessionId,
            response.groupId
          );
        })
      )
      .subscribe({
        next: (event) => {
          if (event.type === 'message' && event.data) {
            this.handleStreamedMessage(event.data);
          } else if (event.type === 'entity_update') {
            // Refetch session to get latest entity state (for checklist updates)
            this.refetchSession(sessionId);
          }
          // 'done' type will complete the observable
        },
        error: (err) => {
          console.error('Failed to send message:', err);
          this.handleSendError();
        },
        complete: () => {
          this.stateService.setSending(false);
          this.currentStreamSubscription = null;
        },
      });
  }

  /**
   * Cancel current streaming subscription
   */
  cancelCurrentStream(): void {
    if (this.currentStreamSubscription) {
      this.currentStreamSubscription.unsubscribe();
      this.currentStreamSubscription = null;
    }
  }

  /**
   * Handle a streamed message from SSE.
   * Updates or adds the message to the session.
   * Tracks analytics events for completed tool calls.
   */
  private handleStreamedMessage(message: ChatMessage): void {
    this.stateService.upsertMessage(message);

    // Track events for newly completed tool calls
    this.trackCompletedToolCalls(message);
  }

  /**
   * Track analytics events for completed tool calls in a message.
   * Only fires events for tool calls we haven't tracked yet.
   */
  private trackCompletedToolCalls(message: ChatMessage): void {
    if (!message.toolCalls || message.toolCalls.length === 0) return;

    for (const tc of message.toolCalls) {
      // Skip if not successful or already tracked
      if (tc.status !== ToolCallStatus.SUCCESS) continue;
      if (this.trackedToolCalls.has(tc.id)) continue;

      const result = tc.result?.raw as StandardToolOutput | undefined;
      if (!result?.entities) continue;

      // Mark as tracked
      this.trackedToolCalls.add(tc.id);

      // Fire appropriate event based on tool name
      switch (tc.name) {
        case 'update_materials_analysis':
          this.amplitudeService.trackEvent('grading_materials_analysis_displayed', {
            gradingId: result.entities['gradingId'],
          });
          break;
        case 'generate_scoring_guide':
          this.amplitudeService.trackEvent('grading_scoring_guide_displayed', {
            scoringGuideId: result.entities['scoringGuideId'],
          });
          break;
        case 'grade_student_works':
          this.amplitudeService.trackEvent('grading_results_displayed', {
            gradingRunId: result.entities['gradingRunId'],
          });
          break;
      }
    }
  }

  /**
   * Refetch the grading session to get latest entity state.
   * Used when entity_update SSE event is received.
   */
  private refetchSession(sessionId: string): void {
    this.gradingApiService
      .getGrading(sessionId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (session) => {
          // Update session while preserving current messages (to avoid flicker during streaming)
          const currentSession = this.stateService.session;
          if (currentSession) {
            this.stateService.setSession({
              ...session,
              messages: currentSession.messages, // Keep current messages during streaming
            });
          } else {
            this.stateService.setSession(session);
          }
        },
        error: (err) => {
          console.error('Failed to refetch session:', err);
        },
      });
  }

  /**
   * Update user message with groupId after API response
   */
  private updateUserMessageWithGroupId(
    tempMessageId: string,
    groupId: string
  ): void {
    const session = this.stateService.session;
    if (session) {
      const messages = session.messages.map((m) =>
        m.id === tempMessageId
          ? { ...m, id: `user-${Date.now()}`, groupId }
          : m
      );
      this.stateService.updateMessages(messages);
    }
  }

  /**
   * Handle error when sending message
   */
  private handleSendError(): void {
    // Remove the optimistic user message on error
    const session = this.stateService.session;
    if (session) {
      const filteredMessages = session.messages.filter(
        (m) => !m.id.startsWith('temp-') && !m.id.startsWith('user-')
      );
      this.stateService.updateMessages(filteredMessages);
    }
    this.stateService.setError('Failed to send message. Please try again.');
    this.stateService.setSending(false);
  }
}
